import { useRef, useEffect, useState } from 'react'
import { Camera, X, RotateCw, RefreshCw } from 'lucide-react'

interface CameraCaptureProps {
  onCapture: (photoData: string) => void
  onCancel: () => void
}

export default function CameraCapture({ onCapture, onCancel }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setErrorMessage(null)
    setHasPermission(null)

    // getUserMedia can hang indefinitely if the permission prompt is
    // ignored/blocked by the OS, or on some in-app browsers. Never let the
    // "Initializing camera..." state spin forever — bail out after 12s
    // with a clear, actionable message and a retry button.
    const timeoutId = setTimeout(() => {
      if (cancelled) return
      cancelled = true
      setHasPermission(false)
      setIsLoading(false)
      setErrorMessage('Camera is taking too long to start. Check that no other app is using it, then try again.')
    }, 12000)

    if (!navigator.mediaDevices?.getUserMedia) {
      clearTimeout(timeoutId)
      setHasPermission(false)
      setIsLoading(false)
      setErrorMessage('Camera is not supported in this browser.')
      return () => {
        cancelled = true
      }
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        clearTimeout(timeoutId)
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
        setHasPermission(true)
        setIsLoading(false)
      })
      .catch((err: any) => {
        if (cancelled) return
        clearTimeout(timeoutId)
        setHasPermission(false)
        setIsLoading(false)
        if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
          setErrorMessage('Camera permission was denied. Please enable camera access for this site in your browser settings and try again.')
        } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
          setErrorMessage('No camera was found on this device.')
        } else if (err?.name === 'NotReadableError' || err?.name === 'TrackStartError') {
          setErrorMessage('Camera is already in use by another app. Close it and try again.')
        } else {
          setErrorMessage('Could not access the camera. Please check your settings and try again.')
        }
      })

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [retryCount])

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d')
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth
        canvasRef.current.height = videoRef.current.videoHeight
        context.drawImage(videoRef.current, 0, 0)
        const photoData = canvasRef.current.toDataURL('image/jpeg')
        setCapturedPhoto(photoData)
      }
    }
  }

  const handleRetake = () => {
    setCapturedPhoto(null)
  }

  const handleConfirm = () => {
    if (capturedPhoto) {
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
      onCapture(capturedPhoto)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="bg-secondary text-white p-4 flex items-center justify-between">
        <h3 className="font-semibold">Live Selfie</h3>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-secondary-light rounded transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 bg-black relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
            <p className="text-white text-center">Initializing camera...</p>
          </div>
        )}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${capturedPhoto ? 'hidden' : ''}`}
        />
        {capturedPhoto && (
          <img
            src={capturedPhoto}
            alt="Captured"
            className="w-full h-full object-cover"
          />
        )}

        {hasPermission === false && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/75 p-6">
            <div className="text-center text-white max-w-xs">
              <Camera className="w-12 h-12 mx-auto mb-4" />
              <p className="mb-2 font-medium">{errorMessage || 'Camera access denied'}</p>
              <button
                onClick={() => setRetryCount((c) => c + 1)}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <div className="bg-white p-4 flex gap-2">
        {!capturedPhoto ? (
          <>
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCapture}
              disabled={!hasPermission || isLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark disabled:bg-neutral-400 text-white rounded-lg font-medium transition-colors"
            >
              <Camera className="w-4 h-4" />
              Capture
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleRetake}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 rounded-lg font-medium transition-colors"
            >
              <RotateCw className="w-4 h-4" />
              Retake
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              Confirm
            </button>
          </>
        )}
      </div>
    </div>
  )
}